import React, { FC, RefObject, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BreakpointsInPx } from '../../../../constants/breakpoints.constants';
import { iconSize } from '../../../../constants/icon.constants';
import useMediaQuery from '../../../../lib/custom-hooks/useMediaQuery';
import { windowService } from '../../../../utils/window.utils';
import Icon from '../../../@shared/icon';
import ModalDialog from '../../../@shared/modalDialog';
import Picture from '../../../@shared/picture';
import { InteractiveImageModel } from '../../models/interactiveImage.model';
import { TInteractiveImageMblDeskItemFieldItemField, TInteractiveImageProps } from '../../types/interactiveImage.types';
import InteractiveSplitModule from '../interactiveSplitModule';

const InteractiveImageHotspot: FC<TInteractiveImageProps & { isEE: boolean; splitModuleContainerRef: RefObject<HTMLDivElement | null> }> = (
  props
) => {
  const model = new InteractiveImageModel(props);
  const isWide = useMediaQuery(`(min-width: ${BreakpointsInPx['md']})`);
  const { hotspotImage, items: hotspotData } = model.mblDeskItem(isWide);
  const [isPopUpOpen, setIsPopUpOpen] = useState(false);
  const [selectedHotspotIndex, setSelectedHotspotIndex] = useState<number | undefined>(undefined);
  const selectedPopUpData: TInteractiveImageMblDeskItemFieldItemField =
    selectedHotspotIndex !== undefined ? hotspotData?.[selectedHotspotIndex]?.fields || {} : {};
  const [popUpStyles, setPopUpStyles] = useState<React.CSSProperties>({});

  const hotspotContainerRef = useRef<HTMLDivElement | null>(null);
  const popUpRef = useRef<HTMLDivElement | null>(null);

  const closePopup = (): void => {
    setIsPopUpOpen(false);
    setSelectedHotspotIndex(undefined);
  };

  const handleHotspotClick = (hotspotIndex: typeof selectedHotspotIndex): void => {
    setSelectedHotspotIndex(hotspotIndex);
    setIsPopUpOpen(true);
  };

  useEffect(() => {
    const updatePopUpPosition = () => {
      if (selectedHotspotIndex === undefined || !isWide) return;

      const hotspotContainerDim = hotspotContainerRef.current?.getBoundingClientRect();
      const selectedHotspotDim = hotspotContainerRef.current?.children.item(selectedHotspotIndex + 1)?.getBoundingClientRect();
      const popUpDim = popUpRef.current?.getBoundingClientRect();

      if (!hotspotContainerDim || !selectedHotspotDim || !popUpDim) return;

      const SPACE_BETWEEN_POPUP_ELEMENT = 32;
      const spaceLeft = selectedHotspotDim.left - hotspotContainerDim.left;
      const spaceRight = hotspotContainerDim.right - selectedHotspotDim.right;
      const spaceTop = selectedHotspotDim.top - hotspotContainerDim.top;
      const spaceBottom = hotspotContainerDim.bottom - selectedHotspotDim.bottom;

      const spaceX = Math.max(spaceLeft, spaceRight) - popUpDim.width;
      const spaceY = Math.max(spaceTop, spaceBottom) - popUpDim.height;

      let popUpTop, popUpLeft;

      if (spaceX > spaceY) {
        popUpLeft =
          spaceLeft > spaceRight
            ? selectedHotspotDim.left - hotspotContainerDim.left - popUpDim.width - SPACE_BETWEEN_POPUP_ELEMENT
            : selectedHotspotDim.right - hotspotContainerDim.left + SPACE_BETWEEN_POPUP_ELEMENT;

        popUpTop = Math.min(
          Math.max(16, selectedHotspotDim.top - hotspotContainerDim.top + (selectedHotspotDim.height - popUpDim.height) / 2),
          hotspotContainerDim.height - popUpDim.height
        );
      } else {
        popUpTop =
          spaceTop > spaceBottom
            ? selectedHotspotDim.top - hotspotContainerDim.top - popUpDim.height - SPACE_BETWEEN_POPUP_ELEMENT
            : selectedHotspotDim.bottom - hotspotContainerDim.top + SPACE_BETWEEN_POPUP_ELEMENT;

        popUpLeft = Math.min(
          Math.max(16, selectedHotspotDim.left - hotspotContainerDim.left + (selectedHotspotDim.width - popUpDim.width) / 2),
          hotspotContainerDim.width - popUpDim.width
        );
      }

      setPopUpStyles({
        left: `${hotspotContainerRef.current?.offsetLeft + popUpLeft}px`,
        top: `${hotspotContainerRef.current?.offsetTop + popUpTop}px`,
        width: `${popUpDim.width}px`,
      });
    };
    updatePopUpPosition();
    windowService()?.addEventListener?.('resize', updatePopUpPosition);
    return () => windowService()?.removeEventListener?.('resize', updatePopUpPosition);
  }, [selectedHotspotIndex, hotspotContainerRef]);

  return (
    <div className="interactive-image__picture-wrapper" ref={hotspotContainerRef}>
      <Picture
        editorialImageProps={{
          desktopImage: hotspotImage,
          desktopTransformation: 'Size1100',
          mobileTransformation: 'Size500'
        }}
      />
      {hotspotData?.map(({ fields }, index) => {
        const { coordinates } = fields || {};
        const [top, left] = coordinates?.value?.split(',').map((_) => _ + '%') || [];
        return (
          <div
            key={`${left}-${top}`}
            style={{ left, top }}
            onClick={(): void => handleHotspotClick(index)}
            className="interactive-image__hotspot-item"
          ></div>
        );
      })}

      {isWide &&
        isPopUpOpen &&
        props.splitModuleContainerRef?.current &&
        createPortal(
          <div className="interactive-image-dsk-popup" ref={popUpRef} style={popUpStyles}>
            <InteractiveSplitModule {...selectedPopUpData} />
            <button className="interactive-image-dsk-popup__close-btn" onClick={closePopup}>
              <Icon iconName="action-close" size={iconSize.dsk} color={'primary'} />
            </button>
          </div>,
          props.splitModuleContainerRef.current
        )}

      {!isWide && selectedPopUpData !== undefined && (
        <ModalDialog isOpen={isPopUpOpen} close={closePopup} skipModalPositionAdjustment={true}>
          <InteractiveSplitModule {...selectedPopUpData} />
        </ModalDialog>
      )}
    </div>
  );
};

export default InteractiveImageHotspot;
